import { Box, Fade, Paper, Typography } from '@mui/material'
import { grey } from '@mui/material/colors'
import { isEqual } from 'lodash'
import { useSnackbar } from 'notistack'
import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useMount, useUnmount } from './hooks/useMount'
import { waitForZoomCleanup } from './SessionCallV2'
import { SessionParticipantsVideoPlayer } from './SessionParticipantsVideoPlayer'
import { useZoomSession } from './SessionProvider'
import { SessionToolbar } from './SessionToolbar'
import { SessionVideoPlayer } from './SessionVideoPlayer'

/**
 * Props interface for the SessionVideoContainer component
 * Defines all the required properties for managing a Zoom video session
 */
interface SessionVideoContainerProps {
  /** Current user participant object containing user info and video state */
  currentUser: any
  /** Function to update the current user state when changes occur */
  setCurrentUser: (user: any) => void
  /** Zoom Stream API instance for managing video/audio streams */
  stream: any
  /** Boolean flag indicating if the current user's video is enabled */
  isVideoOn: boolean
  /** Function to toggle the current user's video on/off state */
  setIsVideoOn: (isVideoOn: boolean) => void
  /** Array of all participants currently in the session */
  allSessionParticipants: any[]
  /** Async function to handle leaving the video call session */
  handleLeaveCall: () => Promise<void>
  /** Zoom VideoClient instance for session management */
  client: any
  /** Object mapping user IDs to their network quality scores (0-3) */
  derivedNetworkQuality: Record<number, number>
}

/**
 * SessionVideoContainer Component
 *
 * Main container component for managing Zoom video session UI and interactions for the current user.
 * This component handles the complete video call experience including:
 * - Video stream management (start/stop video)
 * - Audio stream management (start/stop/mute/unmute audio)
 * - Participant video display layout
 * - Session toolbar with controls
 * - Network quality monitoring
 * - User state synchronization
 *
 * The component automatically starts video on mount and cleans up on unmount.
 * It provides a responsive layout with the current user's video in the top-right
 * corner and other participants' videos in the main area.
 */
export const SessionVideoContainer: React.FC<SessionVideoContainerProps> = ({
  currentUser,
  setCurrentUser,
  stream,
  setIsVideoOn,
  isVideoOn,
  allSessionParticipants,
  handleLeaveCall,
  client,
  derivedNetworkQuality,
}) => {
  const { logger } = useZoomSession()
  const { enqueueSnackbar } = useSnackbar()

  // Get session state and controls from the Zoom session context
  const { activeMicrophone, appointment, isAudioOn, isMuted, setIsMuted, setIsAudioOn } =
    useZoomSession()
  const appointmentProviderId = appointment.physician.provider.user.toString()
  const dispatch = useDispatch()
  // Technically - the provider can join from more than one screen. But keeping the logic
  // simple here and allowing just for one video for the provider and retain current behavior.
  const spotlightUser = allSessionParticipants.find(
    sessionParticipant => sessionParticipant.displayName === appointmentProviderId,
  )
  logger.info('Spotlight user', spotlightUser)

  /**
   * Handles media state changes for the current user
   * Synchronizes the local user state with the Zoom client state when media changes occur
   * This ensures the UI reflects the actual video/audio state from the Zoom SDK
   */
  const handleMediaStateChange = useCallback(async () => {
    // Exit early if current user is not properly initialized
    if (currentUser === null || currentUser.userId === null) return

    logger.info('Zoom: Handling self change')
    const refreshedCurrentUserInfo = client.getCurrentUserInfo()

    // Update current user state if there are meaningful changes
    if (
      refreshedCurrentUserInfo &&
      refreshedCurrentUserInfo?.bVideoOn != null &&
      !isEqual(currentUser, refreshedCurrentUserInfo)
    ) {
      logger.info('Zoom: Previous user info', currentUser)
      logger.info('Zoom: Refreshing current user', refreshedCurrentUserInfo)
      setCurrentUser(refreshedCurrentUserInfo)
    }
  }, [client, currentUser, setCurrentUser])
  /**
   * Initializes and starts the audio stream for the current user
   * Handles Safari-specific audio requirements and microphone device switching
   */
  const handleStartAudio = useCallback(async () => {
    // Start audio automatically in Safari - required for proper audio initialization
    await stream.startAudio({
      autoStartAudioInSafari: true,
      mute: isMuted,
      backgroundNoiseSuppression: true,
      highBitrate: true,
    })
    setIsAudioOn(true)

    /**
     * Switch to user's preferred microphone if different from default
     * The Zoom SDK doesn't allow passing device ID directly to startAudio,
     * so we need to start with default and then switch if needed.
     * This ensures the user's preferred microphone is used for the session.
     */
    const defaultMicrophone = stream.getActiveMicrophone()
    if (
      !!defaultMicrophone &&
      !!activeMicrophone &&
      activeMicrophone?.label !== defaultMicrophone
    ) {
      return stream.switchMicrophone(activeMicrophone?.deviceId)
    }
  }, [activeMicrophone, isMuted, setIsAudioOn, stream])

  /**
   * Initializes and starts the video stream with optimal settings
   * Configures high-quality video and handles errors gracefully
   */
  const handleStartVideo = useCallback(async () => {
    try {
      logger.info('Joining video stream')
      // Wait for any previous Zoom cleanup to complete before starting new video
      await waitForZoomCleanup()

      // Start video with high-quality settings
      await stream.startVideo({
        hd: true, // Enable HD video quality
        fullHd: true, // Enable Full HD if supported
        ptz: stream.isBrowserSupportPTZ(), // Enable pan-tilt-zoom if supported
        originalRatio: true, // Maintain original aspect ratio
        mirrored: true, // Mirror video for natural self-view
      })
      setIsVideoOn(true)
      await handleMediaStateChange()
      logger.info('Joining video stream complete')
    } catch (e) {
      // Handle specific error cases with appropriate user feedback
      if ((e as any)?.reason === 'Camera is starting,please wait.') {
        // Camera is already initializing - show info message
        enqueueSnackbar('Camera is starting, please wait.', {
          variant: 'info',
        })
      } else {
        // General video start failure - show error message
        enqueueSnackbar('Failed to start video', {
          variant: 'error',
        })
      }
      logger.warn('handleStartVideo', e as Error)
    }
  }, [stream, setIsVideoOn, handleMediaStateChange, dispatch])

  const joinZoomAV = useCallback(async () => {
    try {
      logger.info('Joining av stream')
      // Wait for any previous Zoom cleanup to complete before starting new video
      await waitForZoomCleanup()

      if (isVideoOn) {
        await handleStartVideo()
      }
      await handleStartAudio()

      // Sync media state after joining stream
      await handleMediaStateChange()
      logger.info('Self video attachment complete')
      setIsVideoOn(true)
    } catch (e) {
      // Handle specific error cases with appropriate user feedback
      if ((e as any)?.reason === 'Camera is starting,please wait.') {
        // Camera is already initializing - show info message
        enqueueSnackbar('Camera is starting, please wait.', {
          variant: 'info',
        })
      } else {
        // General video start failure - show error message
        enqueueSnackbar('Failed to start video', {
          variant: 'error',
        })
      }
      logger.warn('joinZoomAV', e as Error)
    }
  }, [isVideoOn, handleStartAudio, handleMediaStateChange, handleStartVideo, dispatch])

  /**
   * Stops the current user's video stream and updates state
   * Performs cleanup and state synchronization after stopping video
   */
  const handleStopVideo = useCallback(async () => {
    logger.info('Stopping self video')
    setIsVideoOn(false)
    await stream.stopVideo()
    await handleMediaStateChange()
    logger.info('Stopped self video')
  }, [stream, handleMediaStateChange, setIsVideoOn])

  // Lifecycle hooks: automatically start video on mount and clean up on unmount
  useMount(() => {
    joinZoomAV()
  })
  useUnmount(async () => {
    logger.info('Unmounting Session container.')
    // When the current component unmounts - refresh the state of the current user
    // but do not leave the session yet. The parent component is responsible for handling
    // all clean up activity.
    await handleMediaStateChange()
  })

  /**
   * Stops the audio stream completely
   * Used for cleanup or when audio needs to be fully disabled
   */
  const handleStopAudio = useCallback(async () => {
    await stream.stopAudio()
    setIsAudioOn(false)
  }, [setIsAudioOn, stream])

  /**
   * Mutes the current user's audio while keeping the stream active
   * Updates the local muted state to reflect the change
   */
  const handleMuteAudio = useCallback(async () => {
    await stream.muteAudio()
    setIsMuted(true)
  }, [stream, setIsMuted])

  /**
   * Unmutes the current user's audio
   * Updates the local muted state to reflect the change
   */
  const handleUnmuteAudio = useCallback(async () => {
    await stream.unmuteAudio()
    setIsMuted(false)
  }, [stream, setIsMuted])

  /**
   * Toggles the microphone mute state with robust error handling
   * Handles complex scenarios like starting audio when unmuting if not already active
   * Includes fallback logic to restart audio if unmuting fails
   */
  const handleToggleMicrophone = useCallback(async () => {
    if (isMuted) {
      // Unmuting: start audio if not already active, then unmute
      if (!isAudioOn) await handleStartAudio()
      await handleUnmuteAudio().catch(async () => {
        // Fallback: if unmuting fails, try restarting audio completely
        await handleStopAudio().catch()
        await handleStartAudio().catch()
      })
      await handleMediaStateChange()
    } else {
      // Muting: simply mute the audio
      handleMuteAudio()
    }
  }, [
    isMuted,
    isAudioOn,
    handleStartAudio,
    handleUnmuteAudio,
    handleMediaStateChange,
    handleStopAudio,
    handleMuteAudio,
  ])

  /**
   * Determine feedback message based on session state
   * Shows appropriate loading/waiting messages to users during different phases
   * Note: Both conditions check for empty users array but serve different semantic purposes
   */
  let feedback: string | null = null
  if (!allSessionParticipants.length) feedback = 'Connecting to session'
  if (spotlightUser === undefined) feedback = 'Waiting for other participants'

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        bgcolor: grey[900], // Dark background for video container
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden', // Prevent content overflow
      }}
    >
      {/*
        Video overlay for all provider users - positioned in top-right corner
        This creates a "picture-in-picture" effect where the provider can see themselves
        while the main area shows other participants
      */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: 2,
          position: 'absolute',
          top: 0,
          width: '100%',
          zIndex: 150, // High z-index to appear above other content
        }}
      >
        <Fade in={true}>
          <Paper
            elevation={3}
            sx={{ overflow: 'hidden', position: 'relative', backgroundColor: 'transparent' }}
          >
            {/*
              Note: This uses SessionParticipantsVideoPlayer with users array for self-video
              The isSelfVideo=true flag ensures proper sizing and behavior for the provider users
            */}
            <SessionParticipantsVideoPlayer
              stream={stream}
              users={allSessionParticipants.filter(
                user => user.userGuid !== spotlightUser?.userGuid,
              )}
              networkQuality={derivedNetworkQuality}
            />
          </Paper>
        </Fade>
      </Box>
      {/*
        Main content area - displays the primary participant's video (appointment provider)
        This area takes up the full container and centers the main participant's video
        The appointment provider is typically the physician in the video call
      */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          position: 'absolute',
          height: '100%',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
        }}
      >
        {/*
          Render the appointment provider's video if they are present in the session
          This is the primary participant (usually the physician) and gets the main video area
        */}
        {spotlightUser ? (
          <SessionVideoPlayer
            user={spotlightUser}
            stream={stream}
            networkQuality={derivedNetworkQuality[spotlightUser.userGuid!]}
            isSpotlightVideo={true}
          />
        ) : null}
      </Box>
      {/* Feedback overlay - shows connection/waiting messages when needed */}
      {feedback ? (
        <Box position="absolute" top="45%" width="100%">
          <Box my={2} color="#FFF">
            <Typography align="center">{feedback}</Typography>
          </Box>
        </Box>
      ) : null}

      {/* Session controls toolbar - positioned at bottom of container */}
      <SessionToolbar
        // Camera device switching with error handling
        onChangeCamera={async deviceId => {
          await stream
            .switchCamera(deviceId)
            .catch(e => logger.warn('Zoom: Switch camera failed', e))
        }}
        // Toggle camera on/off
        onToggleCamera={() => {
          isVideoOn ? handleStopVideo() : handleStartVideo()
        }}
        // Microphone device switching (unmutes first to ensure audio is active)
        onChangeMicrophone={async deviceId => {
          await handleUnmuteAudio()
          stream.switchMicrophone(deviceId)
        }}
        // Toggle microphone mute/unmute
        onToggleMicrophone={handleToggleMicrophone}
        // Speaker device switching
        onChangeSpeaker={deviceId => {
          stream.switchSpeaker(deviceId)
        }}
        // Leave call handler
        onLeaveCall={handleLeaveCall}
      />
    </Box>
  )
}
